import { createSkyOrchidOrbit, skyOrchidWaterTransform } from './131-sky-orchid-orbit.mjs';
import { compileSkyAuthoredRig, sampleSkyAuthoredPose } from './115-sky-authored-pose.mjs';
import {
  skyLeviathanMeshSweep,
  skyLeviathanTriangleDistance,
} from './96-sky-leviathan-contacts.mjs';
import { skyAuthoredEnemyPilotContact } from './118-sky-authored-enemies.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

const ROOTS = Array.from({ length: 4 }, (_, i) => `Root_0${i}`);
const DURATION = { approach: 4, track: 3, pollen: 4, recover: 3, sever: 2, bloom: 8 };
const CLIP = {
  approach: 'Idle',
  track: 'Track',
  pollen: 'PollenRelease',
  recover: 'Idle',
  sever: 'RootSever',
  bloom: 'SpiralBloom',
};
export const SKY_ORCHID = Object.freeze({
  cloudLimit: 2,
  seedLimit: 2,
  cloudSpeed: 24,
  cloudRadius: 2.5,
  seedSpeed: 18,
  seedLifetime: 18,
});
const axes = ['x', 'y', 'z'];
const masks = new WeakMap(),
  regions = new WeakMap(),
  seedNoses = new WeakMap();
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const point = (values, index) => ({
  x: values[index * 3],
  y: values[index * 3 + 1],
  z: values[index * 3 + 2],
});
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const pilot = (state) => ({ x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ });
function multiply(a, b) {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}
function rotate(rest, x, z) {
  return multiply(
    rest,
    multiply([Math.sin(x / 2), 0, 0, Math.cos(x / 2)], [0, 0, Math.sin(z / 2), Math.cos(z / 2)]),
  );
}
function facing(direction) {
  const length = Math.hypot(direction.x, direction.y, direction.z) || 1;
  const x = direction.x / length,
    y = direction.y / length,
    z = direction.z / length;
  if (z > 0.999999) return [0, 1, 0, 0];
  const q = [y, -x, 0, 1 - z],
    n = Math.hypot(...q);
  return q.map((v) => v / n);
}
function rotatePoint(q, p) {
  const a = multiply(multiply(q, [p.x, p.y, p.z, 0]), [-q[0], -q[1], -q[2], q[3]]);
  return { x: a[0], y: a[1], z: a[2] };
}
function surfaceMasks(rig) {
  if (masks.has(rig)) return masks.get(rig);
  const result = Object.fromEntries([...ROOTS, 'Reactor'].map((name) => [name, []]));
  for (const [meshIndex, mesh] of rig.meshes.entries()) {
    if (!mesh.jointNodes) continue;
    const owner = (vertex) => {
      let weight = -1,
        name = '';
      for (let slot = 0; slot < 4; slot++)
        if (mesh.weights[vertex * 4 + slot] > weight) {
          weight = mesh.weights[vertex * 4 + slot];
          name = rig.nodes[mesh.jointNodes[mesh.joints[vertex * 4 + slot]]].name;
        }
      return name;
    };
    for (let i = 0; i < mesh.indices.length; i += 3) {
      const ids = mesh.indices.slice(i, i + 3),
        names = ids.map(owner);
      if (Object.hasOwn(result, names[0]) && names.every((name) => name === names[0]))
        result[names[0]].push({ meshIndex, ids });
    }
  }
  masks.set(rig, result);
  return result;
}
function triangles(boss, name) {
  let cache = regions.get(boss.authoredPose);
  if (!cache) {
    cache = new Map();
    regions.set(boss.authoredPose, cache);
  }
  if (!cache.has(name))
    cache.set(
      name,
      Object.freeze(
        surfaceMasks(boss.authoredRig)[name].map(({ meshIndex, ids }) => {
          const v = boss.authoredPose.meshes[meshIndex].vertices;
          return Object.freeze({
            a: Object.freeze(point(v, ids[0])),
            b: Object.freeze(point(v, ids[1])),
            c: Object.freeze(point(v, ids[2])),
          });
        }),
      ),
    );
  return cache.get(name);
}
function rootTarget(boss, index) {
  const socket = boss.authoredPose.sockets[`${ROOTS[index]}_anchor`];
  let closest = Infinity,
    result = socket;
  for (const t of triangles(boss, ROOTS[index])) {
    const p = {
      x: (t.a.x + t.b.x + t.c.x) / 3,
      y: (t.a.y + t.b.y + t.c.y) / 3,
      z: (t.a.z + t.b.z + t.c.z) / 3,
    };
    const d = distance(p, socket);
    if (d < closest) {
      closest = d;
      result = p;
    }
  }
  return result;
}

/** Validate the original biodome's animated irrigation assembly and actual
 * water curtain. Missing source geometry fails readiness; no wash box is made. */
export function installSkyOrchidIrrigationRig(state, source) {
  const rig = compileSkyAuthoredRig(source);
  if (
    !state ||
    rig?.worldId !== 'iron-orchid-irrigation' ||
    !rig.clips.some((c) => c.name === 'Irrigate') ||
    !rig.sockets.some((s) => s.name === 'Waterfall_wash_center') ||
    !rig.meshes.some((m) => m.name === 'Waterfall_route_curtain')
  )
    return false;
  state.authoredPropRigs ??= {};
  state.authoredPropRigs[rig.worldId] = rig;
  return true;
}
function curtainTriangles(prop) {
  const meshIndex = prop.authoredRig.meshes.findIndex((m) => m.name === 'Waterfall_route_curtain');
  const mesh = prop.authoredPose.meshes[meshIndex],
    result = [];
  for (let i = 0; i < mesh.indices.length; i += 3)
    result.push(
      Object.freeze({
        a: Object.freeze(point(mesh.vertices, mesh.indices[i])),
        b: Object.freeze(point(mesh.vertices, mesh.indices[i + 1])),
        c: Object.freeze(point(mesh.vertices, mesh.indices[i + 2])),
      }),
    );
  return Object.freeze(result);
}

/** Attach real water assemblies and retain original source seed/flower scale.
 * Readiness is all-or-nothing; clocks alone never sever roots or expose the core. */
export function createSkyIronOrchid(state, boss) {
  const irrigation = state.authoredPropRigs?.['iron-orchid-irrigation'],
    seed = state.authoredEnemyRigs?.['enemy-homing-seed'];
  if (
    boss.worldId !== 'iron-orchid' ||
    !irrigation ||
    !seed ||
    !['Idle', 'Track', 'PollenRelease', 'RootSever', 'SpiralBloom', 'ReactorCollapse'].every(
      (name) => boss.authoredRig.clips.some((c) => c.name === name),
    ) ||
    ![...ROOTS, 'Reactor'].every((name) => surfaceMasks(boss.authoredRig)[name].length > 0)
  )
    return false;
  boss.orchid = {
    phase: 'approach',
    age: 0,
    clouds: [],
    pollen: false,
    attachedAt: null,
    severed: [],
    overrides: [],
    washCount: 0,
    seedSerial: 0,
    emitted: false,
    previousPilot: pilot(state),
    tilt: { x: 0, z: 0 },
    waterfalls: [],
  };
  const orbit = createSkyOrchidOrbit(state, boss);
  boss.authoredProps = [-1, 1].map((side) => {
    const transform = skyOrchidWaterTransform(orbit, side, irrigation, state.terrainTravel ?? 0);
    const prop = {
      id: state.nextId++,
      kind: 'authored-prop',
      artVariant: 'iron-orchid-irrigation',
      authoredRig: irrigation,
      rootTransform: transform,
      age: 0,
      hp: 1,
      collisionEnabled: false,
      orchidWaterfall: true,
      side,
    };
    prop.authoredPose = sampleSkyAuthoredPose(irrigation, 'Irrigate', 0, transform);
    prop.waterTriangles = curtainTriangles(prop);
    Object.assign(prop, prop.authoredPose.sockets.Waterfall_wash_center);
    boss.orchid.waterfalls.push({
      id: prop.id,
      center: { x: prop.x, y: prop.y, z: prop.z },
      triangles: prop.waterTriangles,
    });
    return prop;
  });
  // Boss-only admission places the real pilot above the original central
  // irrigation arch. The full journey retains the altitude the player flew.
  const archIndex = irrigation.meshes.findIndex((mesh) => mesh.name === 'Irrigation_source_arch');
  boss.orchid.entryHeight =
    Math.max(
      ...boss.authoredProps.map((prop) => prop.authoredPose.meshes[archIndex].bounds.max[1]),
    ) + 4;
  if (state.startMode === 'boss') {
    state.y = boss.orchid.entryHeight;
    state.locomotion = 'fly';
    boss.orchid.previousPilot = pilot(state);
  }
  return true;
}
function enter(state, phase, event) {
  const boss = state.boss,
    o = boss.orchid;
  o.phase = phase;
  o.age = 0;
  o.emitted = false;
  boss.authoredTransition = boss.authoredPose;
  event(
    state,
    'orchid-phase',
    phase === 'bloom'
      ? 'FULL BLOOM · CLIMB ABOVE THE PETALS'
      : phase === 'sever'
        ? 'ROOT SEVERED · FLOWER TILTING'
        : 'MAGNETIC POLLEN · BAIT SEEDS THROUGH WATER',
  );
}
function overrides(boss) {
  const o = boss.orchid;
  const sum = o.severed.reduce(
    (value, index) => {
      value.x += index < 2 ? -1 : 1;
      value.z += [0, 3].includes(index) ? -1 : 1;
      return value;
    },
    { x: 0, z: 0 },
  );
  o.tilt = { x: sum.x * 0.045 - o.severed.length * 0.035, z: sum.z * 0.05 };
  o.overrides = boss.authoredRig.nodes.flatMap((node, index) => {
    const root = ROOTS.indexOf(node.name),
      stem = /^Stem_[0-3]$/.test(node.name);
    if (root < 0 && !stem) return [];
    const severed = root >= 0 && o.severed.includes(root);
    return [
      {
        node: index,
        position: node.position,
        scale: node.scale,
        quaternion: rotate(
          node.quaternion,
          stem ? o.tilt.x : severed ? 0.65 : 0,
          stem ? o.tilt.z : 0,
        ),
      },
    ];
  });
}

/** Advance source-backed bloom phases. Root loss chooses a persistent physical
 * stem tilt; closed petals and unbroken roots cannot be bypassed with Nova. */
export function stepSkyIronOrchid(state, dt, { event }) {
  const boss = state.boss,
    o = boss.orchid;
  if (!o) return null;
  o.age += dt;
  if (o.age >= DURATION[o.phase]) {
    const next =
      o.severed.length === 4
        ? o.phase === 'bloom'
          ? 'recover'
          : 'bloom'
        : o.phase === 'track'
          ? 'pollen'
          : o.phase === 'pollen'
            ? 'recover'
            : 'track';
    enter(state, next, event);
  }
  overrides(boss);
  const seconds =
    o.phase === 'bloom' ? Math.min(4, o.age) : o.phase === 'sever' ? Math.min(4, o.age * 2) : o.age;
  return {
    clip: CLIP[o.phase],
    seconds,
    exposed: o.phase === 'bloom' && o.age >= 2.5,
    target: 'Reactor_target',
  };
}
function emitCloud(state) {
  const boss = state.boss,
    o = boss.orchid,
    from = boss.authoredPose.sockets.Pollen_emitter,
    to = pilot(state);
  const d = distance(from, to) || 1;
  o.clouds.push({
    id: state.nextId++,
    from: { ...from },
    head: { ...from },
    velocity: Object.fromEntries(
      axes.map((axis) => [axis, ((to[axis] - from[axis]) / d) * SKY_ORCHID.cloudSpeed]),
    ),
    age: 0,
    radius: SKY_ORCHID.cloudRadius,
  });
  if (o.clouds.length > SKY_ORCHID.cloudLimit) o.clouds.shift();
}
function spawnSeed(state) {
  const boss = state.boss,
    o = boss.orchid;
  if (boss.authoredProps.filter((p) => p.orchidSeed && p.hp > 0).length >= SKY_ORCHID.seedLimit)
    return;
  const rig = state.authoredEnemyRigs['enemy-homing-seed'],
    source = boss.authoredPose.sockets.Pollen_emitter;
  const seed = {
    id: state.nextId++,
    kind: 'authored-prop',
    artVariant: 'enemy-homing-seed',
    authoredRig: rig,
    orchidSeed: true,
    hp: 3,
    maxHp: 3,
    age: 0,
    mode: 'pursuit',
    targetRoot: null,
    collisionEnabled: true,
    serial: o.seedSerial++,
    ...source,
  };
  seed.rootTransform = {
    position: [seed.x, seed.y, seed.z],
    quaternion: [0, 1, 0, 0],
    scale: [1, 1, 1],
  };
  poseSeed(seed, 'Seek');
  boss.authoredProps.push(seed);
}
function poseSeed(seed, clip) {
  const duration = seed.authoredRig.clips.find((c) => c.name === clip).duration;
  seed.authoredPose = sampleSkyAuthoredPose(
    seed.authoredRig,
    clip,
    seed.age % duration,
    seed.rootTransform,
  );
  const socket = seed.authoredPose.sockets.Seed_sensor;
  seed.weakpoint = {
    ...socket,
    id: 'Seed_sensor',
    radius: 0.5,
    criticalRadius: 0.3,
    exposed: true,
  };
  seed.weakpoints = [seed.weakpoint];
  seed.vulnerable = true;
  const b = seed.authoredPose.bounds;
  seed.body = {
    x: seed.x,
    y: seed.y,
    z: b.max[2],
    radius: Math.max(...b.max.map((v, i) => v - b.min[i])) / 2,
  };
}
function nose(seed) {
  let vertex = seedNoses.get(seed.authoredRig);
  if (!vertex) {
    const socket = seed.authoredPose.sockets.Seed_sensor;
    let best = Infinity;
    seed.authoredPose.meshes.forEach((mesh, meshIndex) => {
      for (let i = 0; i < mesh.vertices.length; i += 3) {
        const d = distance(point(mesh.vertices, i / 3), socket);
        if (d < best) {
          best = d;
          vertex = { meshIndex, index: i / 3 };
        }
      }
    });
    seedNoses.set(seed.authoredRig, vertex);
  }
  // The sensor and its closest nose vertex share the original rigid body.
  // Read that same actual posed vertex; wing articulation cannot relocate it.
  return point(seed.authoredPose.meshes[vertex.meshIndex].vertices, vertex.index);
}

function wash(state, event) {
  const boss = state.boss,
    o = boss.orchid,
    current = pilot(state);
  if (!o.pollen) return;
  const water = boss.authoredProps.find(
    (p) =>
      p.orchidWaterfall &&
      skyLeviathanMeshSweep(
        o.previousPilot,
        current,
        p.waterTriangles,
        SKY_WORLD.pilotContactRadius,
      ),
  );
  if (!water) return;
  o.pollen = false;
  o.washCount++;
  o.washedAt = state.time;
  const available = [0, 1, 2, 3].filter((index) => !o.severed.includes(index));
  for (const seed of boss.authoredProps.filter(
    (p) => p.orchidSeed && p.hp > 0 && p.mode === 'pursuit',
  )) {
    const root = available.sort(
      (a, b) => distance(rootTarget(boss, a), current) - distance(rootTarget(boss, b), current),
    )[0];
    if (root === undefined) {
      seed.mode = 'washed';
      seed.age = 0;
      continue;
    }
    seed.mode = 'root';
    seed.targetRoot = root;
    seed.age = 0;
    const index = available.indexOf(root);
    available.splice(index, 1);
  }
  event(state, 'orchid-wash', 'POLLEN WASHED · SEEDS SEEK THE NEAREST ROOT');
}
function cloudContact(from, to, p, radius) {
  const delta = axes.map((a) => to[a] - from[a]),
    length = delta.reduce((n, v) => n + v * v, 0);
  const t = length
    ? clamp(axes.reduce((n, a, i) => n + (p[a] - from[a]) * delta[i], 0) / length, 0, 1)
    : 0;
  return Math.hypot(...axes.map((a, i) => p[a] - from[a] - delta[i] * t)) <= radius;
}
function stepClouds(state, dt, event) {
  const boss = state.boss,
    o = boss.orchid;
  for (const cloud of o.clouds) {
    const before = { ...cloud.head };
    cloud.age += dt;
    for (const axis of axes) cloud.head[axis] += cloud.velocity[axis] * dt;
    if (
      cloudContact(before, cloud.head, pilot(state), cloud.radius + SKY_WORLD.pilotContactRadius)
    ) {
      cloud.age = 9;
      const position = pilot(state);
      // Remaining inside running water protects the ship but cannot farm
      // seeds: pollen must attach in open air before a deliberate wash pass.
      if (
        boss.authoredProps.some(
          (prop) =>
            prop.orchidWaterfall &&
            skyLeviathanMeshSweep(
              position,
              position,
              prop.waterTriangles,
              SKY_WORLD.pilotContactRadius,
            ),
        )
      )
        continue;
      o.pollen = true;
      o.attachedAt = state.time;
      spawnSeed(state);
      event(state, 'orchid-pollen', 'POLLEN ATTACHED · FLY THROUGH A WATERFALL');
    }
  }
  o.clouds = o.clouds.filter((c) => c.age < 8);
}
function stepSeeds(state, dt, { event, hit }) {
  const boss = state.boss,
    o = boss.orchid;
  for (const seed of boss.authoredProps.filter((p) => p.orchidSeed && p.hp > 0)) {
    seed.age += dt;
    if (seed.age > SKY_ORCHID.seedLifetime || seed.mode === 'washed') {
      seed.hp = 0;
      continue;
    }
    if (seed.mode === 'root' && o.severed.includes(seed.targetRoot)) {
      seed.mode = 'washed';
      continue;
    }
    const target = seed.mode === 'root' ? rootTarget(boss, seed.targetRoot) : pilot(state),
      before = nose(seed);
    let delta = { x: target.x - seed.x, y: target.y - seed.y, z: target.z - seed.z };
    let orientation = facing(delta);
    if (seed.mode === 'root') {
      // Guide the actual source nose onto the root face. Guiding only the mesh
      // origin can leave an offset nose beside a stationary root until expiry.
      // Keep the final approach attitude stable rather than orbiting the point.
      const previous = seed.rootTransform.quaternion;
      if (distance(before, target) < seed.body.radius * 2) orientation = previous;
      const offset = rotatePoint(
        orientation,
        rotatePoint([-previous[0], -previous[1], -previous[2], previous[3]], {
          x: before.x - seed.x,
          y: before.y - seed.y,
          z: before.z - seed.z,
        }),
      );
      delta = Object.fromEntries(
        axes.map((axis) => [axis, target[axis] - seed[axis] - offset[axis]]),
      );
    }
    const length = Math.hypot(delta.x, delta.y, delta.z) || 1;
    for (const axis of axes)
      seed[axis] += (delta[axis] / length) * Math.min(length, SKY_ORCHID.seedSpeed * dt);
    seed.rootTransform = {
      position: [seed.x, seed.y, seed.z],
      quaternion: orientation,
      scale: [1, 1, 1],
    };
    poseSeed(seed, seed.mode === 'root' ? 'WashOff' : 'Seek');
    if (seed.mode === 'root') {
      const contact = skyLeviathanMeshSweep(
        before,
        nose(seed),
        triangles(boss, ROOTS[seed.targetRoot]),
      );
      if (contact) {
        o.severed.push(seed.targetRoot);
        seed.hp = 0;
        enter(state, 'sever', event);
      }
    } else if (o.pollen && skyAuthoredEnemyPilotContact(seed, state)) {
      hit?.(state, 'orchid-seed');
      seed.hp = 0;
    }
  }
  boss.authoredProps = boss.authoredProps.filter((p) => !p.orchidSeed || p.hp > 0);
}

/** Run after the shared owner samples the current source pose. Pollen attaches
 * on real flight contact; washing uses original curtain triangles. Only a source
 * seed nose crossing a real weighted root surface severs that root. */
export function finishSkyIronOrchid(state, dt, { event, hit }) {
  const boss = state.boss,
    o = boss.orchid;
  if (!o || boss.defeated) return;
  for (const prop of boss.authoredProps.filter((p) => p.orchidWaterfall)) {
    const previous = Math.floor(prop.age * 8);
    prop.age += dt;
    if (Math.floor(prop.age * 8) !== previous)
      prop.authoredPose = sampleSkyAuthoredPose(
        prop.authoredRig,
        'Irrigate',
        prop.age % 4,
        prop.rootTransform,
      );
  }
  if (o.phase === 'pollen' && !o.emitted && o.age >= 0.5) {
    emitCloud(state);
    o.emitted = true;
  }
  stepClouds(state, dt, event);
  wash(state, event);
  stepSeeds(state, dt, { event, hit });
  o.previousPilot = pilot(state);
  boss.controlHint =
    o.phase === 'bloom'
      ? 'FULL BLOOM · CLIMB ABOVE PETALS · STRIKE RED REACTOR'
      : o.pollen
        ? 'POLLEN ATTACHED · A/D ORBIT TO A SIDE WATERFALL'
        : boss.authoredProps.some((p) => p.orchidSeed && p.mode === 'root')
          ? 'WASHED SEED · ROOT IMPACT INBOUND'
          : `${o.severed.length}/4 ROOTS · A/D ORBIT · W/S ALTITUDE · BAIT THEN WASH`;
  if (o.severed.length === 4) {
    const reactor = boss.authoredPose.sockets.Reactor_target;
    // The source reactor faces upward. A genuine roughly45-degree firing
    // approach clears its original petals; a camera offset cannot do this.
    o.attackAltitude = Math.min(
      85,
      reactor.y +
        Math.hypot(reactor.x - state.x, reactor.z - (state.pilotZ ?? SKY_WORLD.pilotZ)) +
        6,
    );
    boss.authoredFlightCeiling = Math.max(boss.authoredFlightCeiling, o.attackAltitude + 1);
  }
}

/** Only earned full bloom exposes actual weighted reactor triangles. Ordinary
 * armor, roots, automated escort shots and Nova cannot manufacture progress.
 * Source seeds remain manually destructible for survival without farmed rewards. */
export function resolveSkyIronOrchidContact(state, target, shot, contact, effect) {
  const boss = state.boss,
    o = boss?.orchid;
  if (!o) return null;
  if (target.orchidSeed) {
    const damage = Math.min(target.hp, effect.damage);
    target.hp -= damage;
    return { handled: true, damage, quality: 'body', disabled: target.hp <= 0 };
  }
  if (target !== boss) return null;
  const exposed =
    o.severed.length === 4 && o.phase === 'bloom' && o.age >= 2.5 && !effect.autonomous;
  const reactor =
    exposed &&
    triangles(boss, 'Reactor').some((t) => skyLeviathanTriangleDistance(contact, t) < 0.0001);
  const damage = reactor ? Math.min(boss.hp, effect.damage * 2.5) : 0;
  boss.hp -= damage;
  return { handled: true, damage, quality: reactor ? 'critical' : 'armor' };
}
